// Woonhuis field-update reducer — extraction step W3 from WoonhuisScreen handleCustomFieldChange.
// Verbatim setData-updater body; SCREEN_ID injected as deps.screenId. The pre-setData validation
// error clears stay in the screen; this function is the pure-shape (but NOT side-effect-free)
// document update.
// Known smell, preserved on purpose: side effects (saveWorkflowData / family-composition sync /
// inboedel step-data writes) run INSIDE the updater. There is NO generic else fallback: unknown
// fieldPaths are a no-op ({ ...prevData }) — Inboedel HAS a path-walker, do not harmonize.

import { cleanUnselectedCoveragePremiums } from './calc';
import { syncFamilyCompositionToOtherProducts } from '../../../utils/familyComposition';
import type { SaaProductId } from '../../../utils/addressSharing';

export interface WoonhuisFieldUpdateDeps {
  risicoAdresGelijk: string;
  selectedProducts: Record<string, boolean | undefined> | undefined;
  getStepData: (id: string) => any;
  saveStepData: (id: string, data: any) => void;
  saveWorkflowData: (stepId: string, displayData: any) => void;
  relationData: any;
  setRelationData: (data: any) => void;
  setIsMonumentProperty: (isMonument: boolean) => void;
  screenId: SaaProductId;
}

export const applyWoonhuisFieldUpdate = (
  prevData: any,
  fieldPath: string,
  value: any,
  deps: WoonhuisFieldUpdateDeps,
): any => {
  const {
    risicoAdresGelijk,
    selectedProducts,
    getStepData,
    saveStepData,
    saveWorkflowData,
    relationData,
    setRelationData,
    setIsMonumentProperty,
    screenId,
  } = deps;

  let newData = { ...prevData };

  // Handle VP_GEBDAT (Geboortedatum)
  if (fieldPath === 'Contractdocument.PP.VP.VP_GEBDAT') {
    const dateNum = value ? Number(value) : null;

    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          VP: {
            ...prevData.Contractdocument?.PP?.VP,
            VP_GEBDAT: dateNum
          }
        }
      },
      Relatiedocument: {
        ...prevData.Relatiedocument,
        VP: {
          ...prevData.Relatiedocument?.VP,
          VP_GEBDAT: dateNum
        }
      }
    };

    // Also sync back to relationData for other products/screens to use
    if (dateNum) {
      // Convert N8 format (YYYYMMDD number) to DD-MM-YYYY format (matching client information)
      const dateStr = String(dateNum);
      if (dateStr.length === 8) {
        const year = dateStr.substring(0, 4);
        const month = dateStr.substring(4, 6);
        const day = dateStr.substring(6, 8);
        const displayDate = `${day}-${month}-${year}`; // DD-MM-YYYY
        setRelationData({
          ...relationData,
          clientInfo: {
            ...relationData?.clientInfo,
            dateOfBirth: displayDate
          }
        });
      }
    }

  }
  // Handle WM_INKJHKW (Netto jaarinkomen)
  else if (fieldPath === 'Contractdocument.PP.WM.WM_INKJHKW') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          WM: {
            ...prevData.Contractdocument?.PP?.WM,
            WM_INKJHKW: value ? Number(value) : null
          }
        }
      }
    };
  }
  // Handle OP_GSS (Gezinssamenstelling)
  else if (fieldPath === 'Contractdocument.PP.OP.OP_GSS') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          OP: {
            ...prevData.Contractdocument?.PP?.OP,
            OP_GSS: value
          }
        }
      }
    };

    // Persist OP_GSS immediately so it survives navigation
    saveWorkflowData(screenId, cleanUnselectedCoveragePremiums(newData));

    // Family composition is picked once and shared with every other selected product,
    // regardless of workflow order (not just Inboedel).
    syncFamilyCompositionToOtherProducts(screenId, value, selectedProducts, getStepData, saveStepData);
  }
  // Handle OP_VERZSOM (Verzekerd bedrag)
  else if (fieldPath === 'Contractdocument.PP.OP.OP_VERZSOM') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          OP: {
            ...prevData.Contractdocument?.PP?.OP,
            OP_VERZSOM: value ? Number(value) : null
          }
        }
      }
    };
  }
  // Handle OP_STORM (Afkoop eigen risico storm)
  else if (fieldPath === 'Contractdocument.PP.OP.OP_STORM') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          OP: {
            ...prevData.Contractdocument?.PP?.OP,
            OP_STORM: value,
            // When storm deductible is waived (J), amount is 0; when not waived (N), amount is 250
            OP_ERBSTRM: value === 'J' ? 0 : 250
          }
        }
      }
    };
  }
  // Handle OP_ERBSTRM (Bedrag eigen risico stormschade)
  else if (fieldPath === 'Contractdocument.PP.OP.OP_ERBSTRM') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          OP: {
            ...prevData.Contractdocument?.PP?.OP,
            OP_ERBSTRM: value != null ? Number(value) : null
          }
        }
      }
    };
  }
  // Handle OP_GARONDV (Garantie tegen onderverzekering)
  else if (fieldPath === 'Contractdocument.PP.OP.OP_GARONDV') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          OP: {
            ...prevData.Contractdocument?.PP?.OP,
            OP_GARONDV: value
          }
        }
      }
    };
  }
  // Handle OP_ERB (Bedrag eigen risico)
  else if (fieldPath === 'Contractdocument.PP.OP.OP_ERB') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          OP: {
            ...prevData.Contractdocument?.PP?.OP,
            OP_ERB: value != null ? Number(value) : null
          }
        }
      }
    };
  }
  // Handle BA_STRAAT - updates BA_STRAAT and VP_STRAAT (when addresses are same), syncs to Inboedel
  else if (fieldPath === 'Contractdocument.PP.BA.BA_STRAAT') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_STRAAT: value
          },
          ...(risicoAdresGelijk === 'J' ? {
            VP: {
              ...prevData.Contractdocument?.PP?.VP,
              VP_STRAAT: value
            }
          } : {})
        }
      }
    };

    // Sync to Inboedel if it exists (single address for customer)
    if (selectedProducts?.inboedel) {
      const inboedelData = getStepData('inboedel');
      if (inboedelData) {
        inboedelData.Contractdocument.PP.BA.BA_STRAAT = value;
        if (risicoAdresGelijk === 'J') inboedelData.Contractdocument.PP.VP.VP_STRAAT = value;
        saveStepData('inboedel', inboedelData);
      }
    }
  }
  // Handle BA_PLAATS - updates BA_PLAATS and VP_PLAATS (when addresses are same), syncs to Inboedel
  else if (fieldPath === 'Contractdocument.PP.BA.BA_PLAATS') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_PLAATS: value
          },
          ...(risicoAdresGelijk === 'J' ? {
            VP: {
              ...prevData.Contractdocument?.PP?.VP,
              VP_PLAATS: value
            }
          } : {})
        }
      }
    };

    // Sync to Inboedel if it exists (single address for customer)
    if (selectedProducts?.inboedel) {
      const inboedelData = getStepData('inboedel');
      if (inboedelData) {
        inboedelData.Contractdocument.PP.BA.BA_PLAATS = value;
        if (risicoAdresGelijk === 'J') inboedelData.Contractdocument.PP.VP.VP_PLAATS = value;
        saveStepData('inboedel', inboedelData);
      }
    }
  }
  // Handle BA_BOUWJR (Bouwjaar)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_BOUWJR') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_BOUWJR: value ? Number(value) : null
          }
        }
      }
    };

    // Sync to Inboedel if it exists
    if (selectedProducts?.inboedel) {
      const inboedelData = getStepData('inboedel');
      if (inboedelData) {
        inboedelData.Contractdocument.PP.BA.BA_BOUWJR = value ? Number(value) : null;
        saveStepData('inboedel', inboedelData);
      }
    }
  }
  // Handle BA_TYPEWON (Soort woning)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_TYPEWON') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_TYPEWON: value
          }
        }
      }
    };

    // Sync to Inboedel if it exists
    if (selectedProducts?.inboedel) {
      const inboedelData = getStepData('inboedel');
      if (inboedelData) {
        inboedelData.Contractdocument.PP.BA.BA_TYPEWON = value;
        saveStepData('inboedel', inboedelData);
      }
    }
  }
  // Handle BA_AARDBW (Bouwaard muren)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_AARDBW') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_AARDBW: value
          }
        }
      }
    };

    // Sync to Inboedel if it exists
    if (selectedProducts?.inboedel) {
      const inboedelData = getStepData('inboedel');
      if (inboedelData) {
        inboedelData.Contractdocument.PP.BA.BA_AARDBW = value;
        saveStepData('inboedel', inboedelData);
      }
    }
  }
  // Handle BA_AARDDAK (Bouwaard dak)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_AARDDAK') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_AARDDAK: value
          }
        }
      }
    };

    // Sync to Inboedel if it exists
    if (selectedProducts?.inboedel) {
      const inboedelData = getStepData('inboedel');
      if (inboedelData) {
        inboedelData.Contractdocument.PP.BA.BA_AARDDAK = value;
        saveStepData('inboedel', inboedelData);
      }
    }
  }
  // Handle BA_BGM2 (Oppervlakte begane grond in m2)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_BGM2') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_BGM2: value ? Number(value) : null
          }
        }
      }
    };
  }
  // Handle BA_BDRFBEL (Soort belendend gebouw)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_BDRFBEL') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_BDRFBEL: value
          }
        }
      }
    };

    // Sync to Inboedel if it exists
    if (selectedProducts?.inboedel) {
      const inboedelData = getStepData('inboedel');
      if (inboedelData) {
        inboedelData.Contractdocument.PP.BA.BA_BDRFBEL = value;
        saveStepData('inboedel', inboedelData);
      }
    }
  }
  // Handle BA_EIGENWO (Eigen woning)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_EIGENWO') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_EIGENWO: value
          }
        }
      }
    };

    // Sync to Inboedel if it exists
    if (selectedProducts?.inboedel) {
      const inboedelData = getStepData('inboedel');
      if (inboedelData) {
        inboedelData.Contractdocument.PP.BA.BA_EIGENWO = value;
        saveStepData('inboedel', inboedelData);
      }
    }
  }
  // Handle BA_PERMBEW (Permanent bewoond)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_PERMBEW') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_PERMBEW: value
          }
        }
      }
    };

    // Sync to Inboedel if it exists
    if (selectedProducts?.inboedel) {
      const inboedelData = getStepData('inboedel');
      if (inboedelData) {
        inboedelData.Contractdocument.PP.BA.BA_PERMBEW = value;
        saveStepData('inboedel', inboedelData);
      }
    }
  }
  // Handle BA_TOPPBGB (Totaaloppervlakte bijgebouwen)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_TOPPBGB') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_TOPPBGB: value ? Number(value) : null
          }
        }
      }
    };
  }
  // Handle BA_MONUMEN (Monumentaal pand)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_MONUMEN') {
    const isMonument = value === 'J';
    setIsMonumentProperty(isMonument);
    const baUpdate: any = {
      ...prevData.Contractdocument?.PP?.BA,
      BA_MONUMEN: value
    };
    // Clear BA_SRTMON when monument is set to 'N'
    if (!isMonument) {
      delete baUpdate.BA_SRTMON;
    }
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: baUpdate
        }
      }
    };

    // Sync to Inboedel if it exists
    if (selectedProducts?.inboedel) {
      const inboedelData = getStepData('inboedel');
      if (inboedelData) {
        inboedelData.Contractdocument.PP.BA.BA_MONUMEN = value;
        saveStepData('inboedel', inboedelData);
      }
    }
  }
  // Handle BA_WONING (Gebruik pand)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_WONING') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_WONING: value
          }
        }
      }
    };

    // Sync to Inboedel if it exists
    if (selectedProducts?.inboedel) {
      const inboedelData = getStepData('inboedel');
      if (inboedelData) {
        inboedelData.Contractdocument.PP.BA.BA_WONING = value;
        saveStepData('inboedel', inboedelData);
      }
    }
  }
  // Handle BA_HERBVST (Vaststelling herbouwwaarde)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_HERBVST') {
    // Auto-set OP_GARONDV: 'M' (Waardebepaling maatschappij) = 'J' (yes), others = 'N' (no)
    const garondv = value === 'M' ? 'J' : 'N';
    // Clear taxatie fields when not 'V' (Vaste taxatie)
    const baUpdate: any = { ...prevData.Contractdocument?.PP?.BA, BA_HERBVST: value };
    if (value !== 'V') {
      delete baUpdate.BA_EINDGTX;
    }
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: baUpdate,
          OP: {
            ...prevData.Contractdocument?.PP?.OP,
            OP_GARONDV: garondv
          }
        }
      },
      Supportsection: {
        ...prevData.Supportsection,
        Divers: {
          ...prevData.Supportsection?.Divers,
          ...(value !== 'V' ? { TaxatieVRT: undefined } : {})
        }
      }
    };
  }
  // Handle BA_AARDVLR (Bouwaard vloeren) — woonhuis-only field, no inboedel sync
  else if (fieldPath === 'Contractdocument.PP.BA.BA_AARDVLR') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_AARDVLR: value
          }
        }
      }
    };
  }
  // Handle BA_EINDGTX (Einddatum geldigheid taxatie)
  else if (fieldPath === 'Contractdocument.PP.BA.BA_EINDGTX') {
    newData = {
      ...prevData,
      Contractdocument: {
        ...prevData.Contractdocument,
        PP: {
          ...prevData.Contractdocument?.PP,
          BA: {
            ...prevData.Contractdocument?.PP?.BA,
            BA_EINDGTX: value
          }
        }
      }
    };
  }
  // Handle Supportsection.Divers.TaxatieVRT (Taxatie door VRT-taxateur)
  else if (fieldPath === 'Supportsection.Divers.TaxatieVRT') {
    newData = {
      ...prevData,
      Supportsection: {
        ...prevData.Supportsection,
        Divers: {
          ...prevData.Supportsection?.Divers,
          TaxatieVRT: value
        }
      }
    };
  }
  // Handle Dekkingen - Glas
  else if (fieldPath === 'Supportsection.Dekkingen.Glas') {
    newData = {
      ...prevData,
      Supportsection: {
        ...prevData.Supportsection,
        Dekkingen: {
          ...prevData.Supportsection?.Dekkingen,
          Glas: value
        }
      }
    };
  }
  // Note: Auto-calculation removed to prevent race conditions and network flooding
  // Use the manual "Calculate Premium" button to trigger calculations

  return newData;
};
